import { API } from "@aws-amplify/api";

import { DAY1_ONBOARDING_STEPS, STEP_ID } from "@/components/constants";
import { AppState, getEnabledSteps } from "@/stores/constants";

import { AMPLIFY_DEFAULT_API_NAME } from "../amplifyConfig";

export const CONNECT_TO_VPN_STEP_NUM =
  getEnabledSteps(DAY1_ONBOARDING_STEPS).findIndex(
    (step) => step.id === STEP_ID.CONNECT_VPN
  ) + 1;

/**
 * Return true if users are on a CORP network. Otherwise, we should get a 403 error if users are not on a VPN.
 * connection. This method is not metrics instrumented because it's a polling API and tracking success/failure/latency
 * is not helpful for metrics exposed to developers. The endpoint will have a backend canary testing it to ensure
 * it's online.
 *
 * NOTE: WAF is not enabled in Beta/Gamma so you can't easily test this live using those API endpoints.
 */
export const isCorpNetworkConnected = async (
  state: AppState
): Promise<boolean> => {
  // VPN connectivity is required on the Connect VPN step and again on the
  // Amazon Account Caching step (Windows), since caching domain credentials
  // needs to reach a domain controller. Only poll on those steps so the hook
  // can be mounted broadly without generating traffic elsewhere.
  const isVpnRequiredStep =
    state.currentStepNum === CONNECT_TO_VPN_STEP_NUM ||
    state.currentStep?.id === STEP_ID.AMAZON_ACCOUNT_CACHING;

  if (!isVpnRequiredStep) {
    return Promise.resolve(false);
  }

  // If we already know the network is connected, don't keep pinging it. This should be taken care of by
  // the refetchInterval being set to undefined in src/hooks/useCheckVPNConnectivity.ts getRefetchInterval
  // but double check.
  if (state.isNetworkConnected) {
    return Promise.resolve(true);
  }

  try {
    // Any 2xx response will be considered as a valid VPN connection.
    await API.get(AMPLIFY_DEFAULT_API_NAME, "/user/corp", {});

    return true;
  } catch (err) {
    // Don't bother remote logging because this is a polling API and will fill the log with noise.
    console.warn(err);
    return Promise.reject(false);
  }
};
